/**
 * space/decor.js — decorations, themes and the music choice (GDD 9.3, 9.4).
 *
 * Decorations go into predefined anchors rather than being freely draggable:
 * inside the tank they sit on the gravel, in the room they hang on the walls.
 * Every anchor carries a small plus/minus plaque that opens a picker, so the
 * whole catalogue is reachable without a menu system bolted on top of the world.
 *
 * Seasonal themes are listed but locked, exactly as the GDD describes them —
 * visible as something to come back for, not purchasable now.
 *
 * @module space/decor
 */

import * as THREE from 'three';
import { SPACE_DECOR, ROOM_THEMES, SPACE_MUSIC } from '../constants.js';
import * as factory from '../assets3d/factory.js';
import { pointer, menuHost } from './pointer.js';

/** @param {string} id @returns {object|undefined} */
export function decorSpec(id) {
  return SPACE_DECOR.find((d) => d.id === id);
}

// ─────────────────────────────────────────────────────────────────────────────
// Mesh builders. Each returns a Group with an optional `userData.animate(t)`.
// ─────────────────────────────────────────────────────────────────────────────

const BUILDERS = {
  coral(color) {
    const g = new THREE.Group();
    for (let i = 0; i < 4; i++) {
      const arm = new THREE.Mesh(
        new THREE.ConeGeometry(0.018, 0.1 + (i % 3) * 0.04, 6),
        new THREE.MeshStandardMaterial({ color, roughness: 0.8 }),
      );
      arm.position.set(Math.cos(i * 1.6) * 0.03, 0.06 + (i % 3) * 0.02, Math.sin(i * 1.6) * 0.03);
      arm.rotation.z = Math.cos(i * 1.6) * 0.5;
      arm.rotation.x = -Math.sin(i * 1.6) * 0.5;
      g.add(arm);
    }
    return g;
  },

  plants(color) {
    const g = new THREE.Group();
    const blades = [];
    for (let i = 0; i < 5; i++) {
      const blade = new THREE.Mesh(
        new THREE.BoxGeometry(0.016, 0.16 + (i % 3) * 0.05, 0.005),
        new THREE.MeshStandardMaterial({ color, roughness: 0.85, side: THREE.DoubleSide }),
      );
      blade.position.set((i - 2) * 0.022, 0.09 + (i % 3) * 0.025, 0);
      g.add(blade);
      blades.push(blade);
    }
    g.userData.animate = (t) => {
      blades.forEach((b, i) => { b.rotation.z = Math.sin(t * 1.3 + i) * 0.22; });
    };
    return g;
  },

  castle(color) {
    const g = new THREE.Group();
    const stone = new THREE.MeshStandardMaterial({ color, roughness: 0.95 });
    const keep = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.11, 0.08), stone);
    keep.position.y = 0.055;
    g.add(keep);
    for (const sx of [-1, 1]) {
      const tower = new THREE.Mesh(new THREE.CylinderGeometry(0.026, 0.03, 0.15, 8), stone);
      tower.position.set(sx * 0.06, 0.075, 0);
      g.add(tower);
      const roof = new THREE.Mesh(
        new THREE.ConeGeometry(0.036, 0.05, 8),
        new THREE.MeshStandardMaterial({ color: 0xc94f7c, roughness: 0.8 }),
      );
      roof.position.set(sx * 0.06, 0.175, 0);
      g.add(roof);
    }
    return g;
  },

  bubbler(color) {
    const g = new THREE.Group();
    const base = new THREE.Mesh(
      new THREE.BoxGeometry(0.07, 0.03, 0.04),
      new THREE.MeshStandardMaterial({ color: 0x3a4152, roughness: 0.7 }),
    );
    base.position.y = 0.015;
    g.add(base);

    const bubbles = [];
    const mat = new THREE.MeshStandardMaterial({
      color, transparent: true, opacity: 0.6, roughness: 0.1, depthWrite: false,
    });
    for (let i = 0; i < 9; i++) {
      const b = new THREE.Mesh(new THREE.SphereGeometry(0.007 + (i % 3) * 0.003, 6, 5), mat);
      b.userData.offset = i / 9;
      g.add(b);
      bubbles.push(b);
    }
    g.userData.animate = (t) => {
      for (const b of bubbles) {
        const k = ((t * 0.34 + b.userData.offset) % 1);
        b.position.set(Math.sin(k * 9 + b.userData.offset * 6) * 0.014, 0.03 + k * 0.42, 0);
        b.material.opacity = 0.6 * (1 - k * 0.7);
      }
    };
    return g;
  },

  'treasure-chest': function chest(color) {
    const g = new THREE.Group();
    const wood = new THREE.MeshStandardMaterial({ color: 0x6b4a2a, roughness: 0.9 });
    const box = new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.06, 0.07), wood);
    box.position.y = 0.03;
    g.add(box);

    const lidPivot = new THREE.Group();
    lidPivot.position.set(0, 0.06, -0.035);
    g.add(lidPivot);
    const lid = new THREE.Mesh(new THREE.BoxGeometry(0.115, 0.022, 0.075), wood);
    lid.position.z = 0.035;
    lidPivot.add(lid);

    const gold = new THREE.Mesh(
      new THREE.SphereGeometry(0.028, 8, 6),
      new THREE.MeshStandardMaterial({
        color, roughness: 0.25, metalness: 0.8, emissive: color, emissiveIntensity: 0.35,
      }),
    );
    gold.scale.set(1.4, 0.5, 1);
    gold.position.y = 0.055;
    g.add(gold);

    // Opens for a couple of seconds every eight, then closes again.
    g.userData.animate = (t) => {
      const k = (t % 8) / 8;
      const open = k < 0.12 ? k / 0.12 : k < 0.36 ? 1 : k < 0.48 ? 1 - (k - 0.36) / 0.12 : 0;
      lidPivot.rotation.x = -open * 1.15;
      gold.material.emissiveIntensity = 0.2 + open * 0.6;
    };
    return g;
  },

  'fairy-lights': function fairy(color) {
    const g = new THREE.Group();
    const bulbs = [];
    const count = 14;
    for (let i = 0; i < count; i++) {
      const k = i / (count - 1);
      const bulb = new THREE.Mesh(
        new THREE.SphereGeometry(0.035, 7, 6),
        new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 1 }),
      );
      bulb.position.set((k - 0.5) * 3.2, -Math.sin(k * Math.PI) * 0.28, 0);
      g.add(bulb);
      bulbs.push(bulb);
    }
    const glow = new THREE.PointLight(color, 3.2, 5, 2);
    g.add(glow);
    g.userData.animate = (t) => {
      bulbs.forEach((b, i) => {
        b.material.emissiveIntensity = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(t * 2.4 + i * 0.7));
      });
      glow.intensity = 2.6 + Math.sin(t * 1.6) * 0.7;
    };
    return g;
  },

  'neon-sign': function neon(color) {
    const g = new THREE.Group();
    const sign = factory.createSign('', {
      w: 1.5, h: 0.42, lines: ['HONK'], size: 88, bg: '#120c1e',
      color: '#ff2ea6', border: 'rgba(255,46,166,0.9)',
    });
    g.add(sign);
    const glow = new THREE.PointLight(color, 4.5, 5.5, 2);
    glow.position.z = 0.25;
    g.add(glow);
    g.userData.animate = (t) => {
      // A neon tube that flickers slightly is worth ten that do not.
      const flicker = Math.sin(t * 13) > 0.93 ? 0.35 : 1;
      glow.intensity = (3.4 + Math.sin(t * 2) * 0.6) * flicker;
      sign.material.opacity = 0.72 + 0.28 * flicker;
      sign.material.transparent = true;
    };
    return g;
  },

  spotlight(color) {
    const g = new THREE.Group();
    const can = new THREE.Mesh(
      new THREE.CylinderGeometry(0.09, 0.13, 0.22, 12, 1, true),
      new THREE.MeshStandardMaterial({ color: 0x2a2f3d, roughness: 0.6, side: THREE.DoubleSide }),
    );
    can.rotation.x = Math.PI * 0.36;
    g.add(can);
    const beam = new THREE.Mesh(
      new THREE.ConeGeometry(0.5, 2.4, 14, 1, true),
      new THREE.MeshBasicMaterial({
        color, transparent: true, opacity: 0.09, side: THREE.DoubleSide, depthWrite: false,
      }),
    );
    beam.position.set(0, -1.05, 0.75);
    beam.rotation.x = Math.PI * 0.36;
    g.add(beam);
    const light = new THREE.PointLight(color, 5, 6, 2);
    light.position.set(0, -0.6, 0.5);
    g.add(light);
    g.userData.animate = (t) => { beam.material.opacity = 0.07 + 0.03 * Math.sin(t * 1.1); };
    return g;
  },
};

/**
 * Everything hanging in one room: the placed decorations, their anchor plaques
 * and the wall board that changes the room theme and the music.
 */
export class DecorSet {
  /**
   * @param {{ room: import('./space-world.js').SpaceRoom, tank: import('./fish-tank.js').FishTank,
   *           readOnly?: boolean, onPlace?: (anchor: string, id: string) => void,
   *           onRemove?: (anchor: string) => void,
   *           onRoomTheme?: (id: string) => void, onMusic?: (id: string) => void,
   *           stars?: () => number }} opts
   */
  constructor(opts) {
    this.room = opts.room;
    this.tank = opts.tank;
    this.readOnly = !!opts.readOnly;
    this.hooks = opts;

    this.group = new THREE.Group();
    this.group.name = 'space-decor';
    this.room.root.add(this.group);

    /** @type {Map<string, {catalogId: string, object3D: THREE.Object3D}>} */
    this.placed = new Map();
    /** @type {Map<string, THREE.Object3D>} */ this.plaques = new Map();
    this._elapsed = 0;
    this._idPrefix = `${this.room.name}-decor`;
  }

  /**
   * @param {{ decor: Array<{anchor: string, catalogId: string, color?: number}>,
   *           roomTheme: string, music: string }} model
   */
  rebuild(model) {
    this.dispose();
    this.model = model;

    for (const anchorId of this.anchorIds()) this._addPlaque(anchorId);
    for (const entry of model.decor) this._place(entry);
    this._buildBoard(model);
  }

  /** @returns {string[]} Every anchor id in this room, tank first. */
  anchorIds() {
    const ids = this.tank.decorAnchors.map((_, i) => `tank-${i}`);
    this.room.anchors.roomDecor.forEach((_, i) => ids.push(`room-${i}`));
    return ids;
  }

  /** @private @returns {{parent: THREE.Object3D, node: THREE.Object3D}|null} */
  _anchorNode(anchorId) {
    const [kind, n] = anchorId.split('-');
    const i = Number(n);
    if (kind === 'tank') {
      const node = this.tank.decorAnchors[i];
      return node ? { parent: this.tank.group, node } : null;
    }
    const node = this.room.anchors.roomDecor[i];
    return node ? { parent: this.room.root, node } : null;
  }

  /** @private */
  _place(entry) {
    const spec = decorSpec(entry.catalogId);
    const at = this._anchorNode(entry.anchor);
    if (!spec || !at) return;

    const build = BUILDERS[spec.id];
    if (!build) return;
    const obj = build(entry.color ?? spec.color);
    obj.name = `decor-${spec.id}`;
    obj.position.copy(at.node.position);
    obj.rotation.y = at.node.rotation.y;
    // The catalogue is authored for a mid-size tank. Left alone, a sunken
    // castle fills half a goldfish bowl and is a speck in a wall aquarium.
    if (entry.anchor.startsWith('tank')) {
      obj.scale.setScalar(THREE.MathUtils.clamp(this.tank.tier.height / 0.62, 0.55, 1.9));
    }
    at.parent.add(obj);

    this.placed.set(entry.anchor, { catalogId: spec.id, object3D: obj });
    this._refreshPlaque(entry.anchor);
  }

  /** @private */
  _addPlaque(anchorId) {
    if (this.readOnly) return;
    const at = this._anchorNode(anchorId);
    if (!at) return;

    const plaque = new THREE.Mesh(
      new THREE.CircleGeometry(anchorId.startsWith('tank') ? 0.045 : 0.11, 16),
      new THREE.MeshBasicMaterial({
        color: 0x6fe7c4, transparent: true, opacity: 0.5, side: THREE.DoubleSide,
      }),
    );
    plaque.position.copy(at.node.position);
    plaque.rotation.y = at.node.rotation.y;
    // Tank plaques sit just outside the glass so they stay clickable.
    if (anchorId.startsWith('tank')) plaque.position.z -= this.tank.tier.depth / 2 + 0.06;
    else plaque.position.y -= 0.42;
    at.parent.add(plaque);
    this.plaques.set(anchorId, plaque);

    pointer.register(plaque, {
      id: `${this._idPrefix}:anchor:${anchorId}`,
      group: 'decor-anchor',
      label: 'Decorate',
      onHover: (on) => { plaque.material.opacity = on ? 0.95 : 0.5; },
      onSelect: () => this.openPicker(anchorId),
    });
  }

  /** @private */
  _refreshPlaque(anchorId) {
    const p = this.plaques.get(anchorId);
    if (p) p.material.color.setHex(this.placed.has(anchorId) ? 0xff9a6f : 0x6fe7c4);
  }

  /**
   * Open the decoration picker for an anchor.
   * @param {string} anchorId
   * @returns {string[]} The catalogue ids offered.
   */
  openPicker(anchorId) {
    const target = anchorId.startsWith('tank') ? 'tank' : 'room';
    const stars = this.hooks.stars ? this.hooks.stars() : 0;
    const items = SPACE_DECOR.filter((d) => d.target === target).map((d) => ({
      id: d.id,
      label: d.name,
      sub: `${d.cost}*`,
      disabled: stars < d.cost,
    }));
    if (this.placed.has(anchorId)) items.push({ id: '__remove', label: 'Take it down', sub: '' });

    const at = this._anchorNode(anchorId);
    const pos = at.node.position.clone();
    if (anchorId.startsWith('tank')) {
      this.tank.group.localToWorld(pos);
      this.room.root.worldToLocal(pos);
      pos.y += 0.85;
      pos.z -= 0.6;
    } else {
      pos.y -= 0.5;
      pos.x *= 0.72;
      pos.z *= 0.72;
    }

    menuHost.open(
      { key: `decor:${anchorId}`, parent: this.room.root, position: pos, rotationY: Math.atan2(-pos.x, -pos.z) },
      {
        title: target === 'tank' ? 'TANK DECOR' : 'ROOM DECOR',
        idPrefix: `${this._idPrefix}-pick-${anchorId}`,
        items,
        onPick: (id) => {
          menuHost.close();
          if (id === '__remove') this.hooks.onRemove?.(anchorId);
          else this.hooks.onPlace?.(anchorId, id);
        },
      },
    );
    return items.map((i) => i.id);
  }

  // ── Theme + music board ───────────────────────────────────────────────────

  /** @private */
  _buildBoard(model) {
    if (this.readOnly) return;
    const anchor = this.room.anchors.board;
    const board = factory.createSign('', {
      w: 1.0, h: 0.5, size: 34,
      lines: ['ROOM STYLE', 'AND MUSIC'],
    });
    board.position.copy(anchor.position);
    board.rotation.y = anchor.rotation.y;
    this.group.add(board);
    this._board = board;

    pointer.register(board, {
      id: `${this._idPrefix}:board`,
      group: 'decor-ui',
      label: 'Room style and music',
      onSelect: () => this.openBoard(),
    });
  }

  /** Open the room-style / music chooser. */
  openBoard() {
    const anchor = this.room.anchors.board;
    const pos = anchor.position.clone();
    pos.x += 0.75;
    pos.y += 0.15;
    menuHost.open(
      { key: 'board', parent: this.room.root, position: pos, rotationY: anchor.rotation.y },
      {
        title: 'YOUR SPACE',
        idPrefix: `${this._idPrefix}-board`,
        items: [
          { id: 'themes', label: 'Floor & walls' },
          { id: 'music', label: 'Background music' },
        ],
        onPick: (id) => (id === 'themes' ? this.openThemes() : this.openMusic()),
      },
    );
  }

  /** Open the floor/wall theme list. Seasonal entries show as locked. */
  openThemes() {
    const anchor = this.room.anchors.board;
    const pos = anchor.position.clone();
    pos.x += 0.75;
    pos.y += 0.15;
    const stars = this.hooks.stars ? this.hooks.stars() : 0;
    menuHost.open(
      { key: 'themes', parent: this.room.root, position: pos, rotationY: anchor.rotation.y },
      {
        title: 'FLOOR & WALLS',
        idPrefix: `${this._idPrefix}-themes`,
        items: ROOM_THEMES.map((t) => ({
          id: t.id,
          label: t.name,
          sub: t.locked ? 'seasonal' : (t.cost ? `${t.cost}*` : 'owned'),
          disabled: t.locked || (t.cost > 0 && stars < t.cost && this.model.roomTheme !== t.id),
        })),
        onPick: (id) => { menuHost.close(); this.hooks.onRoomTheme?.(id); },
      },
    );
  }

  /** Open the music track list. */
  openMusic() {
    const anchor = this.room.anchors.board;
    const pos = anchor.position.clone();
    pos.x += 0.75;
    pos.y += 0.15;
    menuHost.open(
      { key: 'music', parent: this.room.root, position: pos, rotationY: anchor.rotation.y },
      {
        title: 'MUSIC',
        idPrefix: `${this._idPrefix}-music`,
        items: SPACE_MUSIC.map((m) => ({
          id: m.id, label: m.name, sub: this.model.music === m.id ? 'playing' : '',
        })),
        onPick: (id) => { menuHost.close(); this.hooks.onMusic?.(id); },
      },
    );
  }

  // ── Frame ─────────────────────────────────────────────────────────────────

  update(dt) {
    this._elapsed += dt;
    for (const rec of this.placed.values()) rec.object3D.userData.animate?.(this._elapsed);
  }

  /** @returns {Array<{anchor: string, catalogId: string}>} */
  list() {
    return [...this.placed.entries()].map(([anchor, r]) => ({ anchor, catalogId: r.catalogId }));
  }

  dispose() {
    menuHost.close();
    for (const anchorId of this.plaques.keys()) {
      pointer.unregister(`${this._idPrefix}:anchor:${anchorId}`);
    }
    pointer.unregister(`${this._idPrefix}:board`);
    for (const rec of this.placed.values()) rec.object3D.parent?.remove(rec.object3D);
    for (const p of this.plaques.values()) p.parent?.remove(p);
    this.placed.clear();
    this.plaques.clear();
    while (this.group.children.length) this.group.remove(this.group.children[0]);
  }
}
